import { ChatStrings } from './chat.strings';

/**
 * Funções puras de formatação. Sem Angular, sem registo de locale: o chat
 * formata a partir de `time` / `dateTime` (ISO 8601) e nunca dos contadores
 * antigos `hours` / `minutes` (contrato 3).
 *
 * Cópia literal de `imodigi-chat/lib/chat.format.ts`, mais `formatAgo` (faixa
 * "sem resposta há X min" do CRM).
 */

export function toDate(value: any): Date | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function pad(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Dias de calendário entre `d` e agora (0 = hoje, 1 = ontem). */
function daysAgo(d: Date, now: Date): number {
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((start(now) - start(d)) / 86400000);
}

/** "HH:mm". */
export function formatTime(value: any): string {
  const d = toDate(value);
  return d ? pad(d.getHours()) + ':' + pad(d.getMinutes()) : '';
}

/** "dd/mm/yyyy". */
export function formatDate(value: any): string {
  const d = toDate(value);
  return d ? pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear() : '';
}

/** Separador de dia na conversa: "Hoje", "Ontem", ou a data. */
export function formatDayLabel(value: any, s: ChatStrings, now: Date = new Date()): string {
  const d = toDate(value);
  if (!d) {
    return '';
  }
  const ago = daysAgo(d, now);
  if (ago <= 0) {
    return s.today;
  }
  if (ago === 1) {
    return s.yesterday;
  }
  return formatDate(d);
}

/** "há 42 min" / "há 3 h" / "há 2 dias" / "agora mesmo" (faixa de atribuição do CRM). */
export function formatAgo(value: any, s: ChatStrings, now: Date = new Date()): string {
  const d = toDate(value);
  if (!d) {
    return '';
  }
  const minutes = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (minutes < 1) {
    return s.justNow;
  }
  if (minutes < 60) {
    return s.agoMinutes.replace('{n}', String(minutes));
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return s.agoHours.replace('{n}', String(hours));
  }
  return s.agoDays.replace('{n}', String(Math.floor(hours / 24)));
}

/**
 * Secção 9 (regras do WhatsApp): a mensagem enviada em `value` ainda está dentro de
 * uma janela de `seconds` segundos (editar: 15 min; apagar para todos: 60 h). Sem
 * janela (0, servidor antigo) a resposta é sempre não.
 */
export function withinWindow(value: any, seconds: number | undefined, now: number = Date.now()): boolean {
  const d = toDate(value);
  return !!d && !!seconds && seconds > 0 && now - d.getTime() <= seconds * 1000;
}

/** "Marta Sousa" -> "MS". */
export function initials(name?: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(p => !!p);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
}

/** Linha de sistema (contrato 6.2): o texto vem de `code`, nunca do servidor. */
export function formatSystemText(code: string, params: { [k: string]: any } | undefined, s: ChatStrings): string {
  // Assigned to themselves = "assumiu a conversa" (2026-10-27; without taking part
  // before, the server writes `joined` instead).
  const self = code === 'assigned' && !!params && !!params['by'] && params['by'] === params['userAppId'];
  const tpl = (self && s.system['assignedSelf']) || s.system[code];
  if (!tpl) {
    return code;
  }
  // Only names reach the text: a missing name or an id (UUID) becomes "alguém".
  const text = tpl.replace(/\{(\w+)\}/g, (_m, key) => {
    const v = params ? params[key] : undefined;
    const value = v === undefined || v === null ? '' : String(v).trim();
    return value && !SYSTEM_ID_RE.test(value) ? value : s.systemSomeone;
  }).replace(/\s{2,}/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const SYSTEM_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Bytes -> "1,2 MB" / "340 KB". */
export function formatSize(bytes?: number, sizeText?: string): string {
  if (sizeText) {
    return sizeText;
  }
  if (!bytes && bytes !== 0) {
    return '';
  }
  if (bytes >= 1024 * 1024) {
    return (Math.round(bytes / (1024 * 1024) * 10) / 10).toString().replace('.', ',') + ' MB';
  }
  return Math.max(1, Math.round(bytes / 1024)) + ' KB';
}

/**
 * Hora curta da lista (versão 3): "agora", "5 min", "10:44", "Ontem", "Seg",
 * "12/09". Só o CRM a usa; `formatListTime` fica como na biblioteca da PWA.
 */
export function formatShortTime(value: any, s: ChatStrings, now: Date = new Date()): string {
  const d = toDate(value);
  if (!d) {
    return '';
  }
  const ago = daysAgo(d, now);
  if (ago <= 0) {
    const minutes = Math.floor((now.getTime() - d.getTime()) / 60000);
    if (minutes < 1) {
      return s.timeNow;
    }
    if (minutes < 60) {
      return s.timeMinutes.replace('{n}', String(minutes));
    }
    return formatTime(d);
  }
  if (ago === 1) {
    return s.yesterday;
  }
  if (ago < 7) {
    return s.weekdays[d.getDay()] || formatDate(d);
  }
  return pad(d.getDate()) + '/' + pad(d.getMonth() + 1);
}

/** Tom determinístico (0..5) para as iniciais de um avatar sem foto: o mesmo nome dá sempre a mesma cor. */
export function avatarTone(name?: string): number {
  const text = String(name || '');
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 6;
}

/** Minúsculas e sem acentos, para pesquisas tolerantes ("Imovel" encontra "imóvel"). */
export function normalizeSearch(text: string): string {
  const lower = String(text || '').toLowerCase();
  try {
    return lower.normalize('NFD').replace(/[̀-ͯ]/g, '');
  } catch (e) {
    return lower;
  }
}

/**
 * Chave estável de uma mensagem no fio (trackBy, "Não lidas", pesquisa na
 * conversa): o id do servidor, senão o clientMessageId, senão o idTemp.
 * Partilhada pelo contentor e pelo `chat-thread`.
 */
export function messageKey(m: { messageId?: string; clientMessageId?: string; idTemp?: number | string }, index: number): string {
  if (m.messageId) {
    return 'm:' + m.messageId;
  }
  if (m.clientMessageId) {
    return 'c:' + m.clientMessageId;
  }
  return 't:' + (m.idTemp !== undefined && m.idTemp !== null ? m.idTemp : index);
}

/** Papel do outro participante, para o subtítulo do cabeçalho. */
export function roleLabel(role: string | undefined, s: ChatStrings): string {
  if (role === 'USERS') {
    return s.roleClient;
  }
  if (role === 'AGENT') {
    return s.roleAgent;
  }
  return role ? s.roleStaff : '';
}

/**
 * "485 000 €" (separador de milhares fino) ou "1 200 €/mês" no arrendamento.
 * Uma string já formatada passa tal como vem; vazio sem preço.
 */
export function formatPrice(value: string | number | undefined | null, monthly = false): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  if (typeof value !== 'number') {
    return String(value);
  }
  const text = String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' €';
  return monthly ? text + '/mês' : text;
}

/** Temperatura da lead a partir da categoria do score (deals/utils/lead-score.util). */
export type ChatTemperature = 'hot' | 'warm' | 'cold';

export function leadTemperature(category?: string | null): ChatTemperature | null {
  if (category === 'top' || category === 'high') {
    return 'hot';
  }
  if (category === 'medium') {
    return 'warm';
  }
  if (category === 'low') {
    return 'cold';
  }
  return null;
}

/** Variáveis das respostas rápidas e automáticas ({{nome}}, {{imovel}}, {{preco}}, {{consultor}}, {{agencia}}). */
export interface ChatTemplateVars {
  nome?: string;
  imovel?: string;
  preco?: string;
  consultor?: string;
  agencia?: string;
}

/**
 * Substitui as variáveis pelo valor; uma variável sem valor desaparece (e os
 * espaços duplos que deixa). Mesma regra do servidor (rtn chat-template-vars).
 */
export function resolveTemplateVars(text: string, vars: ChatTemplateVars | null | undefined): string {
  if (!text) {
    return text;
  }
  const map: { [key: string]: string | undefined } = (vars || {}) as any;
  const out = text.replace(/\{\{\s*([a-zA-Z]+)\s*\}\}/g, (_m: string, key: string) => {
    const value = map[key.toLowerCase()];
    return value ? String(value) : '';
  });
  return out.replace(/[ \t]{2,}/g, ' ').replace(/ +([,.;:!?])/g, '$1').trim();
}

const PT_DAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const PT_MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function two(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

/** "sáb, 27 set · 10:30" (hora local), vazio sem data válida. */
export function formatWhen(value: Date | string | undefined | null): string {
  if (!value) {
    return '';
  }
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) {
    return '';
  }
  return PT_DAYS[d.getDay()] + ', ' + d.getDate() + ' ' + PT_MONTHS[d.getMonth()] + ' · ' + two(d.getHours()) + ':' + two(d.getMinutes());
}

/** `<input type="date">` / `<input type="time">` a partir de uma data local. */
export function toDateInput(d: Date): string {
  return d.getFullYear() + '-' + two(d.getMonth() + 1) + '-' + two(d.getDate());
}

export function toTimeInput(d: Date): string {
  return two(d.getHours()) + ':' + two(d.getMinutes());
}

/** "2026-09-27" + "10:30" → Date local, ou null. */
export function combineDateTime(date: string, time: string): Date | null {
  if (!date || !time) {
    return null;
  }
  const d = new Date(date + 'T' + time + ':00');
  return isNaN(d.getTime()) ? null : d;
}

/** Ficheiro .ics de um evento único (UTC), para "adicionar ao calendário". */
/** Um atalho de data ("Amanhã 10h") dos modais de visita, horários e lembrete. */
export interface ChatQuickPick {
  label: string;
  at: Date;
  date: string;
  time: string;
}

/** Próxima hora certa, a partir de agora (+ `plusHours`). */
export function nextRoundHour(plusHours = 1, now: Date = new Date()): Date {
  const d = new Date(now.getTime() + plusHours * 3600000);
  d.setMinutes(0, 0, 0);
  return d;
}

/** Horário de trabalho das visitas: 9h–19h, segunda a sábado (o servidor usa o mesmo, fase 2). */
const WORK_START_HOUR = 9;
const WORK_END_HOUR = 19;

function isWorkDay(d: Date): boolean {
  return d.getDay() !== 0;
}

function atHour(base: Date, dayOffset: number, hour: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + dayOffset, hour, 0, 0, 0);
  return d;
}

function dayLabel(d: Date, now: Date, s: ChatStrings): string {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - today.getTime()) / 86400000);
  if (diff === 0) {
    return s.today;
  }
  if (diff === 1) {
    return s.tomorrow;
  }
  return s.weekdays[d.getDay()] || '';
}

function toPick(d: Date, now: Date, s: ChatStrings): ChatQuickPick {
  const minutes = d.getMinutes();
  const hour = minutes === 0 ? d.getHours() + 'h' : toTimeInput(d);
  return { label: dayLabel(d, now, s) + ' ' + hour, at: d, date: toDateInput(d), time: toTimeInput(d) };
}

/** Um horário livre vindo da agenda (ISO) como atalho; null se a data for inválida. */
export function quickPickFromIso(iso: string, now: Date, s: ChatStrings): ChatQuickPick | null {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : toPick(d, now, s);
}

/**
 * Atalhos para marcar / propor uma visita: hoje (a próxima hora certa, se ainda
 * cabe no horário), amanhã de manhã e à tarde, e o sábado seguinte — nunca ao
 * domingo, nunca no passado. No máximo 4.
 */
export function buildQuickPicks(now: Date, s: ChatStrings): ChatQuickPick[] {
  const out: ChatQuickPick[] = [];
  const seen: { [key: string]: boolean } = {};
  const push = (d: Date) => {
    const key = d.getTime();
    if (d.getTime() <= now.getTime() || !isWorkDay(d) || d.getHours() < WORK_START_HOUR || d.getHours() >= WORK_END_HOUR || seen[key] || out.length >= 4) {
      return;
    }
    seen[key] = true;
    out.push(toPick(d, now, s));
  };
  const soon = nextRoundHour(1, now);
  if (soon.getDate() === now.getDate() && soon.getMonth() === now.getMonth()) {
    push(soon);
  }
  // Amanhã (ou o dia útil seguinte): manhã e tarde.
  let next = 1;
  while (!isWorkDay(atHour(now, next, 10))) {
    next++;
  }
  push(atHour(now, next, 10));
  push(atHour(now, next, 15));
  // O sábado que vem, se ainda não estiver na lista.
  const toSaturday = (6 - now.getDay() + 7) % 7 || 7;
  push(atHour(now, toSaturday, 11));
  // Depois de amanhã de manhã, para completar quatro.
  push(atHour(now, next + (isWorkDay(atHour(now, next + 1, 10)) ? 1 : 2), 10));
  return out;
}

/** Atalhos de "enviar mais tarde": daqui a 1 hora, hoje às 18h (se ainda der), amanhã às 9h, segunda às 9h. */
export function buildSchedulePicks(now: Date, s: ChatStrings): ChatQuickPick[] {
  const picks: Date[] = [nextRoundHour(1, now)];
  const today18 = atHour(now, 0, 18);
  if (today18.getTime() > now.getTime() + 30 * 60000) {
    picks.push(today18);
  }
  picks.push(atHour(now, 1, 9));
  const toMonday = (1 - now.getDay() + 7) % 7 || 7;
  picks.push(atHour(now, toMonday, 9));
  const out: ChatQuickPick[] = [];
  picks.forEach(d => {
    if (d.getTime() > now.getTime() && !out.some(p => p.at.getTime() === d.getTime())) {
      out.push(toPick(d, now, s));
    }
  });
  return out.slice(0, 4);
}

/** Atalhos de lembrete: amanhã às 9h, daqui a 3 dias às 9h, segunda que vem às 9h. */
export function buildReminderPicks(now: Date, s: ChatStrings): ChatQuickPick[] {
  const picks = [atHour(now, 1, 9), atHour(now, 3, 9)];
  const toMonday = (1 - now.getDay() + 7) % 7 || 7;
  picks.push(atHour(now, toMonday, 9));
  const out: ChatQuickPick[] = [];
  picks.forEach(d => {
    if (!out.some(p => p.at.getTime() === d.getTime())) {
      out.push(toPick(d, now, s));
    }
  });
  return out;
}

export function buildIcs(opts: { uid: string; title: string; description?: string; location?: string; start: Date; durationMinutes: number }): string {
  const stamp = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  const end = new Date(opts.start.getTime() + (opts.durationMinutes || 60) * 60000);
  const esc = (t: string) => (t || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Imodigi//Chat//PT', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    'UID:' + opts.uid,
    'DTSTAMP:' + stamp(new Date()),
    'DTSTART:' + stamp(opts.start),
    'DTEND:' + stamp(end),
    'SUMMARY:' + esc(opts.title),
    ...(opts.description ? ['DESCRIPTION:' + esc(opts.description)] : []),
    ...(opts.location ? ['LOCATION:' + esc(opts.location)] : []),
    'END:VEVENT', 'END:VCALENDAR'
  ].join('\r\n');
}
